import { isBooleanTrue, isListNotEmpty, isObjectNotEmpty, isStringNotEmpty } from "./objectUtils";
import OrderGroupAddOn from "../models/OrderGroupAddOn";
import { findSupplierMapping } from "./supplierMappingUtils";

/**
 * Calculates the resolved quantity for an add-on based on its quantity mode.
 * @param {string} quantityMode - FIXED, PER_UNIT, or PER_LINE_ITEM
 * @param {number} ruleQuantity - The base quantity from the rule
 * @param {number} lineItemQuantity - The quantity of the triggering line item
 * @returns {number}
 */
export const calculateAddOnQuantity = (quantityMode, ruleQuantity, lineItemQuantity) => {
    switch (quantityMode) {
        case 'PER_UNIT':
            return ruleQuantity * lineItemQuantity;
        case 'PER_LINE_ITEM':
        case 'FIXED':
        default:
            return ruleQuantity;
    }
};

/**
 * Resolves all add-ons for an order group based on product rules and supplier mapping overrides.
 *
 * @param {Object[]} orders - Array of order objects, each with lineItems[] (each lineItem has productId, variantId, quantity)
 * @param {string} supplierId - The coveredEntityId of the pharmacy fulfilling this order group
 * @param {Object[]} products - Array of Product objects for the products referenced in line items (must include addOnRules[])
 * @param {Object[]} variants - Array of variant objects for the line items' variants (must include supplierMappings[])
 * @param {Object[]} addOnVariants - Array of variant objects for the add-on products (must include supplierMappings[])
 * @returns {Object[]} Array of OrderGroupAddOn DTOs
 */
export const resolveAddOnsForOrderGroup = (orders, supplierId, products, variants, addOnVariants) => {
    const candidates = [];

    if (!isListNotEmpty(orders) || !isStringNotEmpty(supplierId)) return [];

    // Phase 1: Collect candidates from product-level addOnRules
    for (const order of orders) {
        if (!isListNotEmpty(order?.lineItems)) continue;

        for (const lineItem of order.lineItems) {
            const product = isListNotEmpty(products)
                ? products.find(p => p.id === lineItem.productId)
                : null;

            if (!isObjectNotEmpty(product) || !isListNotEmpty(product.addOnRules)) continue;

            for (const rule of product.addOnRules) {
                if (rule.enabled === false) continue;
                if (!isStringNotEmpty(rule.productId) || !isStringNotEmpty(rule.variantId)) continue;

                const addOnVariant = isListNotEmpty(addOnVariants)
                    ? addOnVariants.find(v => v.productId === rule.productId && v.id === rule.variantId)
                    : null;

                if (!isObjectNotEmpty(addOnVariant)) continue;

                const supplierMapping = findSupplierMapping(addOnVariant, supplierId);
                if (!isObjectNotEmpty(supplierMapping)) continue; // pharmacy doesn't carry this add-on

                const calculatedQuantity = calculateAddOnQuantity(
                    rule.quantityMode || 'FIXED',
                    rule.quantity || 1,
                    lineItem.quantity || 1
                );

                candidates.push({
                    productId: rule.productId,
                    variantId: rule.variantId,
                    name: addOnVariant.name || null,
                    sku: addOnVariant.sku || null,
                    supplierSku: supplierMapping.supplierRefId || null,
                    supplierRefId: supplierMapping.supplierRefId || null,
                    quantity: calculatedQuantity,
                    quantityMode: rule.quantityMode || 'FIXED',
                    scope: rule.scope || 'PER_PRODUCT',
                    shipmentQuantityMode: rule.shipmentQuantityMode || 'SUM',
                    requiresPrescription: isBooleanTrue(rule.requiresPrescription),
                    source: 'PRODUCT_RULE',
                    sourceProductId: lineItem.productId,
                    sourceVariantId: lineItem.variantId,
                    priority: 2,
                });
            }
        }
    }

    // Phase 2: Collect candidates from supplier mapping overrides on the line item variants
    for (const order of orders) {
        if (!isListNotEmpty(order?.lineItems)) continue;

        for (const lineItem of order.lineItems) {
            const variant = isListNotEmpty(variants)
                ? variants.find(v => v.id === lineItem.variantId)
                : null;

            if (!isObjectNotEmpty(variant)) continue;

            const supplierMapping = findSupplierMapping(variant, supplierId);
            if (!isObjectNotEmpty(supplierMapping) || !isListNotEmpty(supplierMapping.addOns)) continue;

            for (const addOn of supplierMapping.addOns) {
                if (!isStringNotEmpty(addOn.productId) || !isStringNotEmpty(addOn.variantId)) continue;

                // Resolve scope from legacy isUnique if scope is not set
                const resolvedScope = isStringNotEmpty(addOn.scope)
                    ? addOn.scope
                    : (addOn.isUnique === true ? 'PER_SHIPMENT' : 'PER_PRODUCT');
                const resolvedQuantityMode = isStringNotEmpty(addOn.quantityMode)
                    ? addOn.quantityMode
                    : 'FIXED';
                const resolvedShipmentQuantityMode = isStringNotEmpty(addOn.shipmentQuantityMode)
                    ? addOn.shipmentQuantityMode
                    : 'SUM';

                // Look up the add-on variant for supplier SKU resolution
                const addOnVariant = isListNotEmpty(addOnVariants)
                    ? addOnVariants.find(v => v.productId === addOn.productId && v.id === addOn.variantId)
                    : null;

                let addOnSupplierSku = null;
                let addOnSupplierRefId = null;
                if (isObjectNotEmpty(addOnVariant)) {
                    const addOnSupplierMapping = findSupplierMapping(addOnVariant, supplierId);
                    if (isObjectNotEmpty(addOnSupplierMapping)) {
                        addOnSupplierSku = addOnSupplierMapping.supplierRefId || null;
                        addOnSupplierRefId = addOnSupplierMapping.supplierRefId || null;
                    }
                }

                const calculatedQuantity = calculateAddOnQuantity(
                    resolvedQuantityMode,
                    addOn.quantity || 1,
                    lineItem.quantity || 1
                );

                candidates.push({
                    productId: addOn.productId,
                    variantId: addOn.variantId,
                    name: addOn.name || (addOnVariant ? addOnVariant.name : null),
                    sku: addOn.sku || (addOnVariant ? addOnVariant.sku : null),
                    supplierSku: addOnSupplierSku,
                    supplierRefId: addOnSupplierRefId,
                    quantity: calculatedQuantity,
                    quantityMode: resolvedQuantityMode,
                    scope: resolvedScope,
                    shipmentQuantityMode: resolvedShipmentQuantityMode,
                    requiresPrescription: isBooleanTrue(addOn.requiresPrescription),
                    source: 'SUPPLIER_MAPPING',
                    sourceProductId: lineItem.productId,
                    sourceVariantId: lineItem.variantId,
                    priority: 1, // highest priority - manual overrides win
                });
            }
        }
    }

    // Phase 3: Merge and deduplicate
    const groups = {};
    for (const candidate of candidates) {
        const key = `${candidate.productId}::${candidate.variantId}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(candidate);
    }

    const resolvedAddOns = [];

    for (const key of Object.keys(groups)) {
        const groupCandidates = groups[key];

        // Sort by priority ascending (1 = SUPPLIER_MAPPING highest, 2 = PRODUCT_RULE)
        groupCandidates.sort((a, b) => a.priority - b.priority);

        // If any supplier mapping override exists, discard product rules for this add-on
        const hasSupplierOverride = groupCandidates.some(c => c.source === 'SUPPLIER_MAPPING');
        const activeCandidates = hasSupplierOverride
            ? groupCandidates.filter(c => c.source === 'SUPPLIER_MAPPING')
            : groupCandidates;

        // Determine effective scope - if ANY candidate is PER_SHIPMENT, whole group is PER_SHIPMENT
        const effectiveScope = activeCandidates.some(c => c.scope === 'PER_SHIPMENT')
            ? 'PER_SHIPMENT'
            : 'PER_PRODUCT';

        // Use the highest-priority candidate for metadata
        const representative = activeCandidates[0];

        let finalQuantity;
        if (effectiveScope === 'PER_SHIPMENT') {
            const shipmentQtyMode = representative.shipmentQuantityMode || 'SUM';
            if (shipmentQtyMode === 'MAX') {
                finalQuantity = Math.max(...activeCandidates.map(c => c.quantity));
            } else {
                // SUM
                finalQuantity = activeCandidates.reduce((sum, c) => sum + c.quantity, 0);
            }
        } else {
            // PER_PRODUCT: sum all candidate quantities
            finalQuantity = activeCandidates.reduce((sum, c) => sum + c.quantity, 0);
        }

        resolvedAddOns.push(new OrderGroupAddOn({
            name: representative.name,
            sku: representative.sku,
            productId: representative.productId,
            variantId: representative.variantId,
            quantity: finalQuantity,
            refId: null, // populated later when sent to pharmacy
            requiresPrescription: isBooleanTrue(representative.requiresPrescription),
        }).toDto());
    }

    return resolvedAddOns;
};
